'use client'

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { getTournamentChatId } from '@/lib/tournament-chat'
import { UserAvatar } from '@/components/ui/user-avatar'
import { ParticipantsModal } from './participants-modal'

interface Member {
  id: string
  gamertag: string
  profile_image_url: string | null
}

/** A real uploaded picture, as opposed to the GP placeholder. */
function hasAvatar(m: Member): boolean {
  return !!m.profile_image_url && m.profile_image_url.trim() !== ''
}

function StackAvatar({ m }: { m: Member }) {
  return (
    <UserAvatar
      url={m.profile_image_url}
      alt={m.gamertag}
      size={36}
      className="ring-2 ring-card"
      style={{ transform: 'rotate(-12deg)' }}
    />
  )
}

export function TournamentParticipants({
  tournamentId,
  gameSlug,
  confirmed,
  capacity,
  substitutes,
  refreshSignal = 0,
}: {
  tournamentId: string
  /** Drives which per-game stat badges (if any) the modal shows. */
  gameSlug?: string
  confirmed: number
  capacity: number
  substitutes: number
  refreshSignal?: number
}) {
  const [members, setMembers] = useState<Member[]>([])
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const cid = await getTournamentChatId(tournamentId)
      if (!cid) {
        if (!cancelled) setMembers([])
        return
      }
      const { data: mem } = await supabase
        .from('group_chat_members')
        .select('user_id')
        .eq('group_chat_id', cid)
        .limit(200)
      const ids = (mem ?? []).map((m) => m.user_id)
      if (ids.length === 0) {
        if (!cancelled) setMembers([])
        return
      }
      const { data: users } = await supabase
        .from('users')
        .select('id, gamertag, profile_image_url')
        .in('id', ids)
      // Players who uploaded a picture lead the stack: a row of identical GP
      // placeholders says nothing about who is in the lobby, and most events
      // have enough real avatars to fill every visible slot.
      const sorted = ((users ?? []) as Member[]).slice().sort((a, b) => {
        const av = hasAvatar(a) ? 0 : 1
        const bv = hasAvatar(b) ? 0 : 1
        return av - bv
      })
      if (!cancelled) setMembers(sorted)
    })()
    return () => {
      cancelled = true
    }
  }, [tournamentId, refreshSignal])

  const total = confirmed + substitutes
  const shown = members.slice(0, 7)
  const extra = Math.max(0, total - shown.length)

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Participants
        </h3>
        {total > 0 && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="shrink-0 text-sm font-medium text-white underline-offset-2 hover:underline"
          >
            View all
          </button>
        )}
      </div>
      <div className="mt-1 text-2xl font-bold">
        {confirmed}
        <span className="text-sm font-normal text-muted-foreground">
          {' '}
          / {capacity} players
        </span>
      </div>
      {substitutes > 0 && (
        <div className="mt-1 text-xs font-medium text-muted-foreground">
          +{substitutes} substitute{substitutes === 1 ? '' : 's'}
        </div>
      )}

      {shown.length > 0 && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="mt-4 flex w-full items-center transition-opacity hover:opacity-80"
          aria-label="View all participants"
        >
          <div className="flex -space-x-2">
            {shown.map((m) => (
              <StackAvatar key={m.id} m={m} />
            ))}
            {extra > 0 && (
              <div className="flex size-9 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground ring-2 ring-card">
                +{extra}
              </div>
            )}
          </div>
        </button>
      )}

      <ParticipantsModal
        tournamentId={tournamentId}
        gameSlug={gameSlug}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
